import { CommonConstants, ErrorResponse } from "@/common/CommonConstants";
import { DriverWorkCycleEditTemplate } from "@/components/driverWorkCycles/DriverWorkCycleEditTemplate";
import { DriverWorkCycle } from "@/models/common";
import { DriverWorkCycleEdit as EditType } from "@/models/driverWorkShiftsAndCycle";
import { DriverWorkShift } from "@/models/operationPlans";
import { uuidv4 } from "@/util";
import {
  checkEffectiveCycleAPI,
  getDriverWorkCycleAPI,
  updateDriverDailyWorkCyclesAPI,
} from "@/util/api/driverWorkCycles";
import { getDriverWorkShiftsAPI } from "@/util/api/driverWorkShifts";
import { useStatefulNavigator } from "@/util/useStatefulNavigator";
import { AxiosError } from "axios";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DriverWorkCyclesConstants } from "./DriverWorkCyclesConstants";
import toast from "@/components/hoc/SnackbarAlert";
import { Messages } from "@/util/Messages";
import { ConfirmDialog } from "@/components/molecules/ConfirmDialog";
import { Typography } from "@mui/material";
import { getOfficeAPI } from "@/util/api/API";
import { useLoadingPromise } from "@/hooks/useLoadingPromise";

export const DriverWorkCycleEdit = () => {
  const params = useParams<{ office_id: string; cycle_id: string }>();
  const officeId = params.office_id;
  const cycleId = params.cycle_id;

  const { navigate } = useStatefulNavigator();
  const goToDetail = useCallback(() => {
    navigate(`/driver_work_cycles/${officeId}`);
  }, [navigate, officeId]);

  const [driverWorkShifts, setDriverWorkShifts] = useState<DriverWorkShift[]>(
    []
  );
  const [driverWorkCycle, setDriverWorkCycle] = useState<EditType>({});
  const [showWarning, setShowWarning] = useState(false);
  const handleWarnCancel = useCallback(() => {
    setShowWarning(false);
  }, []);

  const [loading, updateDriverDailyWorkCyclesAPIWrapper] = useLoadingPromise(
    updateDriverDailyWorkCyclesAPI
  );

  const [effectiveCycle, setEffectiveCycle] = useState(false);

  const update = useCallback(() => {
    if (
      !driverWorkCycle.shortName?.length ||
      !driverWorkCycle.driverWorkShifts?.length ||
      loading
    ) {
      return;
    }

    const requestShifts = driverWorkCycle.driverWorkShifts
      .map(({ shift }) => shift)
      .filter((shift) => shift != null) as DriverWorkShift[];

    const request: Partial<DriverWorkCycle> = {
      ...driverWorkCycle,
      driverWorkShifts: requestShifts,
    };

    setShowWarning(false);

    updateDriverDailyWorkCyclesAPIWrapper(request)
      .then(goToDetail)
      .catch((e: AxiosError<ErrorResponse>) => {
        if ([404, 409].includes(e.response?.status ?? -1)) {
          toast.error(
            `${CommonConstants.Replace}${Messages.ErrorOccurred}: ${
              e?.response?.data?.message ?? ""
            }`
          );
        }
      });
  }, [
    driverWorkCycle,
    goToDetail,
    loading,
    updateDriverDailyWorkCyclesAPIWrapper,
  ]);

  const handleSubmit = useCallback(() => {
    if (effectiveCycle) {
      setShowWarning(true);
      return;
    }

    update();
  }, [effectiveCycle, update]);

  useEffect(() => {
    if (officeId == null || cycleId == null) {
      return;
    }

    checkEffectiveCycleAPI(Number(cycleId))
      .then((res) => setEffectiveCycle(res.data))
      .catch((e) =>
        console.log(
          "===Error occurred while checking if cycle is effective===",
          e
        )
      );

    getDriverWorkShiftsAPI(Number(officeId))
      .then((res) => setDriverWorkShifts(res.data))
      .catch((e) =>
        console.log("===Error occurred while getting DriverWorkShifts===", e)
      );

    getDriverWorkCycleAPI(Number(cycleId))
      .then((res) => {
        const cycle = res.data;
        const shifts = cycle.driverWorkShifts ?? [];
        getOfficeAPI(Number(officeId))
          .then((sres) =>
            setDriverWorkCycle({
              ...cycle,
              offices: sres.data,
              driverWorkShifts: shifts.map((s, idx) => ({
                number: idx + 1,
                key: uuidv4(),
                shift: s,
              })),
            })
          )
          .catch((e) =>
            console.log("===Error occurred while getting Office===")
          );
      })
      .catch((e) =>
        console.log("===Error occurred while getting DriverWorkCycle===", e)
      );
  }, [cycleId, officeId]);

  return (
    <>
      <DriverWorkCycleEditTemplate
        title={DriverWorkCyclesConstants.DriverWorkCycleEdit}
        driverWorkCycle={driverWorkCycle}
        driverWorkShifts={driverWorkShifts}
        effectiveCycle={effectiveCycle}
        loading={loading}
        onCancel={goToDetail}
        onSubmit={handleSubmit}
        onChangeData={setDriverWorkCycle}
      />
      <ConfirmDialog
        visible={showWarning}
        headerTitle={DriverWorkCyclesConstants.ConfirmTitle}
        showIcon
        type="Normal"
        onCancel={handleWarnCancel}
        onOk={update}
      >
        <Typography gutterBottom sx={{ whiteSpace: "pre-line" }}>
          {DriverWorkCyclesConstants.UpdateWarning}
        </Typography>
      </ConfirmDialog>
    </>
  );
};
